import { forwardRef, useCallback, useState, useEffect, useMemo } from "react";
import { taskService } from "../api"; // Importamos el servicio
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import style from "../style/Tarea.module.css";
import { capitalizarPrimera } from "../utils/texto";
import {
  DEFAULT_PRIORIDADES,
  PRIORIDAD_COLORES,
  colorDePrioridad,
} from "../utils/prioridades";
import {
  filterTasksForDate,
  getTaskDayCode,
  getTaskRepeatDays,
  getTaskTargetDate,
  isTaskCompletedOnDate,
  summarizeTasksForDate,
} from "../utils/tasks";
import {
  FiBarChart2,
  FiCalendar,
  FiChevronDown,
  FiChevronLeft,
  FiChevronRight,
  FiClock,
  FiGrid,
  FiList,
  FiMinus,
  FiMoon,
  FiMoreVertical,
  FiPlus,
  FiSun,
  FiSunrise,
  FiEdit2,
  FiTrash2,
  FiX,
  FiTrendingUp,
  FiTarget,
  FiCheckSquare,
  FiFlag,
  FiUsers,
  FiLogOut,
  FiSearch,
} from "react-icons/fi";
import CompartirTareaModal from "./CompartirTareaModal";


const FormDateButton = forwardRef(({ value, onClick }, ref) => (
  <button
    ref={ref}
    type="button"
    onClick={onClick}
    className={style.formDateButton}
  >
    <span className={style.formDateIcon}>
      <FiCalendar />
    </span>
    <span className={style.formDateCopy}>
      <small>Fecha</small>
      <strong>{value || "Seleccionar fecha"}</strong>
    </span>
    <FiChevronDown className={style.formDateChevron} />
  </button>
));

FormDateButton.displayName = "FormDateButton";

const MOMENTO_OPTIONS = [
  { value: "manana", label: "Mañana", horario: "Mañana", Icon: FiSunrise },
  { value: "tarde", label: "Tarde", horario: "Tarde", Icon: FiSun },
  { value: "noche", label: "Noche", horario: "Noche", Icon: FiMoon },
  { value: "indiferente", label: "Indiferente", horario: "", Icon: FiMinus },
];

const isExactTime = (horario) => /^\d{2}:\d{2}$/.test(String(horario || ""));

const deriveMomento = (horario) => {
  if (!horario) return "indiferente";
  if (horario === "Mañana") return "manana";
  if (horario === "Tarde") return "tarde";
  if (horario === "Noche") return "noche";
  return isExactTime(horario) ? "exacta" : "indiferente";
};

const initialFormData = {
  meta: "",
  fecha: new Date(),
  horario: "",
  momento: "indiferente",
  urgencia: "importante",
  color: "color1",
  esRecurrente: false,
  diasRepeticion: [],
};

const colorOptions = [
  "color1",
  "color2",
  "color3",
  "color4",
  "color5",
  "color6",
  "color7",
  "color8",
  "color9",
  "color10",
];

// ===== Helpers de Calendario / Historial =====
const WEEKDAY_LABELS = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];

const HISTORY_PERIODS = [
  { value: "day", label: "Día" },
  { value: "week", label: "Semana" },
  { value: "month", label: "Mes" },
  { value: "year", label: "Año" },
];

const startOfDay = (value) => {
  const date = new Date(value);
  date.setHours(0, 0, 0, 0);
  return date;
};

const addDays = (value, amount) => {
  const date = new Date(value);
  date.setDate(date.getDate() + amount);
  return date;
};

// Lunes = 0 ... Domingo = 6
const mondayIndex = (date) => (date.getDay() + 6) % 7;

const isSameDay = (a, b) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

// 42 celdas (6 semanas) para la grilla del mes
const buildMonthGrid = (refDate) => {
  const year = refDate.getFullYear();
  const month = refDate.getMonth();
  const first = new Date(year, month, 1);
  const start = addDays(first, -mondayIndex(first));

  return Array.from({ length: 42 }, (_, index) => {
    const date = addDays(start, index);
    return { date, inMonth: date.getMonth() === month };
  });
};

const getPeriodRange = (period, refDate) => {
  const day = startOfDay(refDate);

  if (period === "day") return { from: day, to: day };

  if (period === "week") {
    const from = addDays(day, -mondayIndex(day));
    return { from, to: addDays(from, 6) };
  }

  if (period === "year") {
    return {
      from: new Date(day.getFullYear(), 0, 1),
      to: new Date(day.getFullYear(), 11, 31),
    };
  }

  // month
  return {
    from: new Date(day.getFullYear(), day.getMonth(), 1),
    to: new Date(day.getFullYear(), day.getMonth() + 1, 0),
  };
};

const eachDayInRange = (from, to) => {
  const days = [];
  let current = startOfDay(from);
  const end = startOfDay(to);

  while (current <= end) {
    days.push(current);
    current = addDays(current, 1);
  }

  return days;
};

const shiftPeriod = (period, refDate, direction) => {
  const date = new Date(refDate);

  if (period === "day") date.setDate(date.getDate() + direction);
  else if (period === "week") date.setDate(date.getDate() + direction * 7);
  else if (period === "month") date.setMonth(date.getMonth() + direction);
  else date.setFullYear(date.getFullYear() + direction);

  return date;
};

const formatPeriodLabel = (period, refDate) => {
  const { from, to } = getPeriodRange(period, refDate);

  if (period === "day") {
    return from.toLocaleDateString("es-AR", {
      weekday: "long",
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  }

  if (period === "year") return String(refDate.getFullYear());

  if (period === "month") {
    return from.toLocaleDateString("es-AR", { month: "long", year: "numeric" });
  }

  const opts = { day: "2-digit", month: "short" };
  return `${from.toLocaleDateString("es-AR", opts)} – ${to.toLocaleDateString("es-AR", opts)}`;
};

// Rendimiento del período: solo cuenta días hasta hoy
const summarizePeriod = (tasks, from, to) => {
  const today = startOfDay(new Date());
  const days = eachDayInRange(from, to).filter((day) => day <= today);

  let total = 0;
  let done = 0;

  const perUnit = days.map((day) => {
    const summary = summarizeTasksForDate(tasks, day);
    total += summary.total;
    done += summary.completed;
    return { day, ...summary };
  });

  return {
    total,
    done,
    pending: Math.max(total - done, 0),
    percent: total ? Math.round((done / total) * 100) : 0,
    perUnit,
  };
};

// 30 frases de motivación para hacer tareas: se muestra 1 por día (rota sola).
const FRASES_TAREAS = [
  "Las personas que cumplen sus tareas rinden más que las que las postergan. Hoy te toca a vos.",
  "Cada tarea que tachás es un ladrillo de la persona que querés ser.",
  "La disciplina es elegir lo que querés a largo plazo por sobre lo que querés ahora.",
  "No tenés que hacerlo perfecto, tenés que empezarlo.",
  "Una tarea hecha vale más que diez planeadas.",
  "El futuro se construye con lo que hacés hoy, no mañana.",
  "Los que hacen, avanzan. Los que esperan motivación, siguen igual.",
  "Hecho es mejor que perfecto. Dale para adelante.",
  "Pequeños pasos todos los días te llevan lejos.",
  "La constancia le gana al talento cuando el talento no es constante.",
  "Tu yo del futuro te va a agradecer lo que hagas ahora.",
  "Empezá aunque no tengas ganas: las ganas vienen después.",
  "Ordená tu día y tu cabeza se ordena sola.",
  "No cuentes los días, hacé que los días cuenten.",
  "El progreso, no la perfección, es lo que te mantiene en movimiento.",
  "Lo difícil de hoy es lo fácil de mañana si lo practicás.",
  "Menos excusas, más tareas tachadas.",
  "La motivación te arranca, el hábito te sostiene.",
  "Cada 'sí' a tu tarea es un 'no' a la mediocridad.",
  "Enfocate en una sola cosa y hacela bien.",
  "Los grandes resultados son la suma de pequeñas tareas cumplidas.",
  "Dejá de esperar el momento perfecto: crealo.",
  "Tu energía sigue a tu acción, no al revés.",
  "Cumplir con vos mismo es la mejor forma de subir tu autoestima.",
  "Hoy es un buen día para hacer eso que venís posponiendo.",
  "La suerte aparece cuando la preparación se encuentra con la acción.",
  "Terminar lo que empezás es un superpoder. Usalo.",
  "Un día productivo empieza con una sola tarea bien hecha.",
  "No busques hacer todo, buscá hacer lo importante.",
  "Sé constante en lo chico y lo grande llega solo.",
];

const diaDelAnio = (date) => {
  const inicio = new Date(date.getFullYear(), 0, 0);
  return Math.floor((date - inicio) / 86400000);
};

function Tareas({ refreshKey, onTaskSaved, activeWorkspace = "personal" }) {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  // Vista activa: "day" (diaria) | "calendar" (calendario) | "history" (historial)
  const [viewMode, setViewMode] = useState("day");
  const [error, setError] = useState("");
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [calendarRef, setCalendarRef] = useState(new Date()); // mes que muestra el calendario
  const [historyPeriod, setHistoryPeriod] = useState("month"); // día/semana/mes/año
  const [historyRef, setHistoryRef] = useState(new Date()); // período de referencia del historial
  const [updatingTaskIds, setUpdatingTaskIds] = useState([]);
  const [openTaskMenu, setOpenTaskMenu] = useState(null);
  const [compartirTask, setCompartirTask] = useState(null); // tarea a compartir
  const [invitaciones, setInvitaciones] = useState([]); // invitaciones pendientes
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
  const [dayActionDate, setDayActionDate] = useState(null); // día tocado en el calendario
  const [editingTask, setEditingTask] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  // Prioridades del usuario (nombre + color) y mini-editor para crear/editar una
  const [prioridades, setPrioridades] = useState(DEFAULT_PRIORIDADES);
  // null = cerrado; { original: nombre|null, nombre, color }
  const [prioridadEditor, setPrioridadEditor] = useState(null);
  const [prioridadError, setPrioridadError] = useState("");
  // Colores elegidos a mano con el "+" (se suman a los 5 principales)
  const [prioridadColoresExtra, setPrioridadColoresExtra] = useState([]);
  // Colores de tarea elegidos a mano con el "+" (se suman a los 5 principales)
  const [taskColoresExtra, setTaskColoresExtra] = useState([]);
  const [formError, setFormError] = useState("");
  const [formSuccess, setFormSuccess] = useState("");
  const [formSaving, setFormSaving] = useState(false);
  const [isTaskDatePickerOpen, setIsTaskDatePickerOpen] = useState(false);
  // Filtro de la lista del día: todas / pendientes / completadas
  const [dayFilter, setDayFilter] = useState("all");
  const [isPanelDatePickerOpen, setIsPanelDatePickerOpen] = useState(false);

  const visibleTasks = useMemo(
    () => filterTasksForDate(tasks, selectedDate),
    [tasks, selectedDate]
  );
  // Grilla del mes para la vista calendario
  const calendarCells = useMemo(() => buildMonthGrid(calendarRef), [calendarRef]);

  // Rango y rendimiento del historial (según día/semana/mes/año)
  const periodRange = useMemo(
    () => getPeriodRange(historyPeriod, historyRef),
    [historyPeriod, historyRef]
  );
  const historySummary = useMemo(
    () => summarizePeriod(tasks, periodRange.from, periodRange.to),
    [tasks, periodRange]
  );

  // Resumen del día seleccionado (vista diaria) — normalizado a { total, done, pending, percent }
  const daySummary = useMemo(() => {
    const s = summarizeTasksForDate(tasks, selectedDate);
    return {
      total: s.total,
      done: s.completed,
      pending: s.pending,
      percent: s.total ? Math.round((s.completed / s.total) * 100) : 0,
    };
  }, [tasks, selectedDate]);

  // Resumen del mes que muestra el calendario
  const calendarMonthSummary = useMemo(() => {
    const { from, to } = getPeriodRange("month", calendarRef);
    return summarizePeriod(tasks, from, to);
  }, [tasks, calendarRef]);

  // El anillo de progreso refleja la vista activa
  const activeSummary =
    viewMode === "history"
      ? historySummary
      : viewMode === "calendar"
        ? calendarMonthSummary
        : daySummary;
  const completedTasksCount = activeSummary.done;
  const pendingTasksCount = activeSummary.pending;
  const progressPercent = activeSummary.percent;

  // Comparación de progreso: este mes vs. el mes pasado.
  const comparativaMes = useMemo(() => {
    const hoy = new Date();
    const act = getPeriodRange("month", hoy);
    const ant = getPeriodRange(
      "month",
      new Date(hoy.getFullYear(), hoy.getMonth() - 1, 15)
    );
    const a = summarizePeriod(tasks, act.from, act.to);
    const b = summarizePeriod(tasks, ant.from, ant.to);
    return {
      actual: a.percent,
      anterior: b.percent,
      diff: a.percent - b.percent,
      total: a.total,
      totalAnt: b.total,
    };
  }, [tasks]);

  // Frase de motivación del día (rota 1 por día entre 30).
  const fraseDelDia =
    FRASES_TAREAS[diaDelAnio(new Date()) % FRASES_TAREAS.length];

  const fetchTasks = useCallback(async () => {
    const storedToken = localStorage.getItem("token") || sessionStorage.getItem("token");
    if (!storedToken) return;

    setLoading(true);
    try {
      const res = await taskService.getAll({ tipo: "task", workspace: activeWorkspace });
      setTasks(Array.isArray(res.data) ? res.data : []);
      setError("");
      // Invitaciones a tareas compartidas (banner de arriba).
      taskService
        .invitaciones()
        // Las invitaciones a listas de compras se muestran en Compras, no acá.
        .then(({ data }) => setInvitaciones((data?.invitaciones || []).filter((i) => i.tipo !== "shopping")))
        .catch(() => {});
    } catch (err) {
      setError("No se pudieron cargar las tareas.");
    } finally {
      setLoading(false);
    }
  }, [activeWorkspace]);

  const aceptarInvitacion = async (inv) => {
    setInvitaciones((prev) => prev.filter((x) => x.id !== inv.id));
    try {
      const { data } = await taskService.aceptarInvitacion(inv.id);
      if (data?.tarea) {
        setTasks((prev) => [
          ...prev.filter((t) => String(t._id) !== String(data.tarea._id)),
          data.tarea,
        ]);
      }
    } catch {
      /* no-op */
    }
    fetchTasks();
  };
  const rechazarInvitacion = async (inv) => {
    setInvitaciones((prev) => prev.filter((x) => x.id !== inv.id));
    try {
      await taskService.salir(inv.id);
    } catch {
      /* no-op */
    }
  };
  const handleSalirTarea = async (taskId) => {
    if (!window.confirm("¿Dejar de colaborar en esta tarea?")) return;
    try {
      await taskService.salir(taskId);
      await fetchTasks();
    } catch {
      setError("No se pudo salir de la tarea.");
    }
  };

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks, refreshKey]);

  useEffect(() => {
    let activo = true;
    taskService
      .getPrioridades()
      .then(({ data }) => {
        if (activo && Array.isArray(data?.prioridades) && data.prioridades.length) {
          setPrioridades(data.prioridades);
        }
      })
      .catch(() => {});
    return () => {
      activo = false;
    };
  }, []);

  const getStatusDate = (value) => {
    if (typeof value === "string") {
      return value.slice(0, 10);
    }

    return getTaskTargetDate(value);
  };

  // Navegación del día desde el header (flechas ‹ › y botón HOY)
  const shiftSelectedDay = (delta) =>
    setSelectedDate((prev) => {
      const next = new Date(prev);
      next.setDate(next.getDate() + delta);
      return next;
    });

  const handleToggleCompleteForDate = async (taskId, dateValue = selectedDate) => {
    const fecha = getStatusDate(dateValue);
    const targetTask = tasks.find((task) => task._id === taskId);

    if (!targetTask || updatingTaskIds.includes(taskId)) {
      return;
    }

    const previousCompletadasEn = Array.isArray(targetTask.completadasEn)
      ? targetTask.completadasEn
      : [];
    const nextCompletadasEn = previousCompletadasEn.includes(fecha)
      ? previousCompletadasEn.filter((item) => item !== fecha)
      : [...previousCompletadasEn, fecha];

    setUpdatingTaskIds((prev) => [...prev, taskId]);
    setTasks((prev) =>
      prev.map((task) =>
        task._id === taskId
          ? {
              ...task,
              completadasEn: nextCompletadasEn,
            }
          : task
      )
    );

    try {
      const res = await taskService.updateStatus(taskId, { fecha });
      setTasks((prev) =>
        prev.map((task) =>
          task._id === taskId
            ? {
                ...task,
                ...res.data,
                completadasEn: Array.isArray(res.data?.completadasEn)
                  ? res.data.completadasEn
                  : nextCompletadasEn,
              }
            : task
        )
      );
    } catch (err) {
      setTasks((prev) =>
        prev.map((task) =>
          task._id === taskId
            ? {
                ...task,
                completadasEn: previousCompletadasEn,
              }
            : task
        )
      );
      console.error("Error al actualizar estado");
    } finally {
      setUpdatingTaskIds((prev) => prev.filter((id) => id !== taskId));
    }
  };

  const handleToggleComplete = (taskId) => {
    handleToggleCompleteForDate(taskId, selectedDate);
  };

  const handleDeleteTask = async (taskId) => {
    if (!window.confirm("¿Eliminar tarea?")) return;
    try {
      await taskService.delete( taskId);
      setTasks((prev) => prev.filter((t) => t._id !== taskId));
      setError("");
    } catch (err) {
      console.error("Error al eliminar");
      setError("No se pudo eliminar la tarea.");
    }
  };

  const handleEditTask = (task) => {
    const fechaUTC = new Date(task.fecha || new Date());
    fechaUTC.setMinutes(fechaUTC.getMinutes() + fechaUTC.getTimezoneOffset());

    setEditingTask(task);
    setFormData({
      ...initialFormData,
      ...task,
      fecha: fechaUTC,
      momento: deriveMomento(task.horario),
      diasRepeticion: getTaskRepeatDays(task.diasRepeticion),
    });
    setFormError("");
    setFormSuccess("");
    setIsTaskModalOpen(true);
  };

  const handleOpenNewTask = (presetDate) => {
    // Si viene una fecha válida (ej. desde el calendario), la pre-cargamos
    const fecha =
      presetDate instanceof Date && !Number.isNaN(presetDate.getTime())
        ? new Date(presetDate)
        : new Date();

    setEditingTask(null);
    setFormData({ ...initialFormData, fecha });
    setFormError("");
    setFormSuccess("");
    setIsTaskDatePickerOpen(false);
    setIsTaskModalOpen(true);
  };

  const handleCloseTaskModal = () => {
    setIsTaskModalOpen(false);
    setEditingTask(null);
    setFormData(initialFormData);
    setFormError("");
    setFormSuccess("");
    setIsTaskDatePickerOpen(false);
  };

  const handleFormChange = (event) => {
    const { name, value, type, checked } = event.target;
    if (name === "esRecurrente") {
      setFormData((prev) => {
        const currentDays = getTaskRepeatDays(prev.diasRepeticion);
        const currentDay = getTaskDayCode(prev.fecha);

        return {
          ...prev,
          esRecurrente: checked,
          diasRepeticion: checked
            ? currentDays.length > 0
              ? currentDays
              : currentDay
                ? [currentDay]
                : []
            : [],
        };
      });
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]:
        type === "checkbox"
          ? checked
          : name === "meta"
            ? capitalizarPrimera(value)
            : value,
    }));
  };

  const handleTaskDateChange = (date) => {
    if (!date) return;
    setFormData((prev) => ({ ...prev, fecha: date }));
    setIsTaskDatePickerOpen(false);
  };

  const handleColorSelect = (color) => {
    setFormData((prev) => ({ ...prev, color }));
  };

  const abrirNuevaPrioridad = () => {
    setPrioridadError("");
    setPrioridadEditor({ original: null, nombre: "", color: PRIORIDAD_COLORES[0] });
  };

  const abrirEditarPrioridad = (p) => {
    setPrioridadError("");
    setPrioridadEditor({ original: p.nombre, nombre: p.nombre, color: p.color });
  };

  const guardarPrioridades = async (lista, nombreElegido) => {
    const { data } = await taskService.savePrioridades(lista);
    const nuevas = Array.isArray(data?.prioridades) && data.prioridades.length ? data.prioridades : lista;
    setPrioridades(nuevas);
    if (nombreElegido !== undefined) {
      setFormData((prev) => ({ ...prev, urgencia: nombreElegido }));
    }
    setPrioridadEditor(null);
  };

  const confirmarPrioridad = async () => {
    if (!prioridadEditor) return;
    const nombre = prioridadEditor.nombre.trim();
    if (!nombre) {
      setPrioridadError("Escribí un nombre.");
      return;
    }
    const repetida = prioridades.some(
      (p) =>
        p.nombre.toLowerCase() === nombre.toLowerCase() &&
        p.nombre !== prioridadEditor.original
    );
    if (repetida) {
      setPrioridadError("Ya existe una prioridad con ese nombre.");
      return;
    }
    const lista = prioridadEditor.original
      ? prioridades.map((p) =>
          p.nombre === prioridadEditor.original ? { nombre, color: prioridadEditor.color } : p
        )
      : [...prioridades, { nombre, color: prioridadEditor.color }];
    try {
      await guardarPrioridades(lista, nombre);
    } catch (err) {
      setPrioridadError(err?.response?.data?.message || "No se pudo guardar la prioridad.");
    }
  };

  const eliminarPrioridad = async () => {
    if (!prioridadEditor?.original) return;
    if (prioridades.length <= 1) {
      setPrioridadError("Tiene que quedar al menos una prioridad.");
      return;
    }
    const lista = prioridades.filter((p) => p.nombre !== prioridadEditor.original);
    try {
      await guardarPrioridades(
        lista,
        formData.urgencia === prioridadEditor.original ? lista[0].nombre : undefined
      );
    } catch (err) {
      setPrioridadError(err?.response?.data?.message || "No se pudo eliminar la prioridad.");
    }
  };

  const handleMomentoSelect = (value) => {
    setFormData((prev) => {
      if (value === "exacta") {
        return {
          ...prev,
          momento: "exacta",
          horario: isExactTime(prev.horario) ? prev.horario : "09:00",
        };
      }

      const option = MOMENTO_OPTIONS.find((item) => item.value === value);
      return { ...prev, momento: value, horario: option ? option.horario : "" };
    });
  };

  const toggleRepeatDay = (dia) => {
    setFormData((prev) => {
      const currentDays = getTaskRepeatDays(prev.diasRepeticion);

      return {
        ...prev,
        diasRepeticion: currentDays.includes(dia)
          ? currentDays.filter((item) => item !== dia)
          : [...currentDays, dia],
      };
    });
  };

  const handleTaskSubmit = async (event) => {
    event.preventDefault();
    setFormError("");
    setFormSuccess("");

    if (!formData.meta.trim()) {
      setFormError("Escribe el nombre de la tarea.");
      return;
    }

    const repeatDays = getTaskRepeatDays(formData.diasRepeticion);

    if (formData.esRecurrente && repeatDays.length === 0) {
      setFormError("Elige al menos un dia para repetir la tarea.");
      return;
    }

    setFormSaving(true);

    try {
      const fechaLocal = new Date(formData.fecha);
      fechaLocal.setMinutes(fechaLocal.getMinutes() - fechaLocal.getTimezoneOffset());

      const payload = {
        ...formData,
        meta: formData.meta.trim(),
        tipo: "task",
        workspace: activeWorkspace,
        fecha: fechaLocal.toISOString().slice(0, 10),
        diasRepeticion: formData.esRecurrente ? repeatDays : [],
      };

      if (editingTask?._id) {
        await taskService.update(editingTask._id, payload);
        setFormSuccess("Tarea actualizada.");
      } else {
        await taskService.create(payload);
        setFormSuccess("Tarea creada.");
      }

      await fetchTasks();
      onTaskSaved?.();

      setTimeout(() => {
        handleCloseTaskModal();
      }, 450);
    } catch (err) {
      setFormError(err.response?.data?.message || "No se pudo guardar la tarea.");
    } finally {
      setFormSaving(false);
    }
  };

  const isTaskCompleted = (task) => {
    return isTaskCompletedOnDate(task, selectedDate);
  };

  // Orden de agenda: horas exactas cronológicas, después momentos
  // (Mañana/Tarde/Noche) y al final las que no tienen horario.
  const MOMENTO_RANK = { "Mañana": 8 * 60, "Tarde": 14 * 60, "Noche": 20 * 60 };
  const agendaKey = (t) => {
    const h = t.horario || "";
    if (isExactTime(h)) {
      const [hh, mm] = h.split(":").map(Number);
      return hh * 60 + mm;
    }
    if (MOMENTO_RANK[h] != null) return MOMENTO_RANK[h];
    return Infinity; // sin horario al final
  };
  const agendaLabel = (h) => {
    if (isExactTime(h)) return h;
    if (h === "Mañana" || h === "Tarde" || h === "Noche") return h;
    return "Sin hora";
  };

  const renderContent = () => {
    if (loading) return <p className={style.emptyMessage}>Cargando tareas...</p>;
    if (error) return <p className={style.errorMessage}>{error}</p>;
    const filtradas = visibleTasks.filter((t) => {
      if (dayFilter === "pending") return !isTaskCompleted(t);
      if (dayFilter === "done") return isTaskCompleted(t);
      return true;
    });
    if (filtradas.length === 0)
      return (
        <p className={style.emptyMessage}>
          {visibleTasks.length === 0
            ? "Aun no tienes tareas. Anade una para comenzar."
            : dayFilter === "pending"
              ? "No quedan tareas pendientes. ¡Bien ahí!"
              : "Todavía no completaste ninguna tarea hoy."}
        </p>
      );

    const ordenadas = [...filtradas].sort((a, b) => agendaKey(a) - agendaKey(b));

    return (
      <div className={style.agenda}>
        {ordenadas.map((task) => {
      const completed = isTaskCompleted(task);

      const menuOpen = openTaskMenu === task._id;
      const sinHora = !isExactTime(task.horario) && !["Mañana", "Tarde", "Noche"].includes(task.horario);

      return (
        <div className={style.agendaRow} key={task._id}>
          <div className={`${style.agendaTime} ${sinHora ? style.agendaTimeSoft : ""}`}>
            <span className={style.agendaTimeLabel}>{agendaLabel(task.horario)}</span>
            <span className={style.agendaDot} />
          </div>
          <div
            className={`${style.taskCard} ${
              completed ? style.completed : ""
            } ${style[task.color] || style.color1}`}
            style={(() => {
              // La tarjeta entera se pinta con el color de la prioridad; si la
              // prioridad no tiene color, con el color propio de la tarea.
              const fondo =
                colorDePrioridad(prioridades, task.urgencia) ||
                (task.color?.startsWith?.("#") ? task.color : null);
              return fondo ? { background: fondo } : undefined;
            })()}
          >
            <div className={style.taskTop}>
              {/* Izquierda: opciones (tres puntitos) */}
              <button
                type="button"
                className={style.taskOptionsBtn}
                onClick={() => setOpenTaskMenu(menuOpen ? null : task._id)}
                aria-label="Opciones"
                aria-expanded={menuOpen}
              >
                <FiMoreVertical />
              </button>

              <div className={style.taskBody}>
                <p className={style.taskMeta}>{task.meta}</p>
                <div className={style.taskMetaRow}>
                  <span className={`${style.taskChip} ${style.taskDate}`}>
                    <FiCalendar />
                    {task.fecha ? task.fecha.slice(0, 10) : "-"}
                  </span>
                  <span className={`${style.taskChip} ${style.taskUrgency}`}>
                    {task.urgencia || "Normal"}
                  </span>
                  <span className={`${style.taskChip} ${style.taskSchedule}`}>
                    <FiClock />
                    {task.horario || "Sin horario"}
                  </span>
                  {task.compartida ? (
                    <span className={`${style.taskChip} ${style.taskShared}`}>
                      <FiUsers />
                      {task.soyOwner === false ? "Compartida conmigo" : "Compartida"}
                    </span>
                  ) : null}
                </div>
              </div>

              {/* Derecha: check circular (se pinta verde al completar) */}
              <button
                type="button"
                className={`${style.taskCheckCircle} ${completed ? style.taskCheckDone : ""}`}
                onClick={() => handleToggleComplete(task._id)}
                disabled={updatingTaskIds.includes(task._id)}
                aria-label={completed ? "Marcar como pendiente" : "Completar"}
              />
            </div>

            {/* Fila desplegable con editar / compartir / eliminar */}
            {menuOpen ? (
              <div className={style.taskExpanded}>
                <button
                  type="button"
                  onClick={() => {
                    setOpenTaskMenu(null);
                    handleEditTask(task);
                  }}
                  className={style.taskExpandedBtn}
                >
                  <FiEdit2 /> Editar
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setOpenTaskMenu(null);
                    setCompartirTask(task);
                  }}
                  className={style.taskExpandedBtn}
                >
                  <FiUsers /> Compartir
                </button>
                {task.soyOwner === false ? (
                  <button
                    type="button"
                    onClick={() => {
                      setOpenTaskMenu(null);
                      handleSalirTarea(task._id);
                    }}
                    className={`${style.taskExpandedBtn} ${style.taskExpandedDelete}`}
                  >
                    <FiLogOut /> Salir
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => {
                      setOpenTaskMenu(null);
                      handleDeleteTask(task._id);
                    }}
                    className={`${style.taskExpandedBtn} ${style.taskExpandedDelete}`}
                  >
                    <FiTrash2 /> Eliminar
                  </button>
                )}
              </div>
            ) : null}
          </div>
        </div>
      );
        })}
      </div>
    );
  };

  const renderCalendar = () => {
    if (loading) return <p className={style.emptyMessage}>Cargando tareas...</p>;
    if (error) return <p className={style.errorMessage}>{error}</p>;

    const today = new Date();

    return (
      <div className={style.calendarMode}>
        <div className={style.calendarNav}>
          <button
            type="button"
            onClick={() => setCalendarRef((prev) => shiftPeriod("month", prev, -1))}
            aria-label="Mes anterior"
          >
            <FiChevronLeft />
          </button>
          <span>
            {calendarRef.toLocaleDateString("es-AR", { month: "long", year: "numeric" })}
          </span>
          <button
            type="button"
            onClick={() => setCalendarRef((prev) => shiftPeriod("month", prev, 1))}
            aria-label="Mes siguiente"
          >
            <FiChevronRight />
          </button>
        </div>

        <div className={style.calendarWeekdays}>
          {WEEKDAY_LABELS.map((d) => (
            <span key={d}>{d}</span>
          ))}
        </div>

        <div className={style.calendarGrid}>
          {calendarCells.map(({ date, inMonth }, index) => {
            const dayTasks = filterTasksForDate(tasks, date);
            const isToday = isSameDay(date, today);

            return (
              <button
                type="button"
                key={index}
                className={`${style.calendarCell} ${inMonth ? "" : style.calendarCellMuted} ${
                  isToday ? style.calendarCellToday : ""
                }`}
                onClick={() => setDayActionDate(date)}
                title={`${dayTasks.length} tarea(s)`}
              >
                <span className={style.calendarDayNumber}>{date.getDate()}</span>
                <span className={style.calendarDots}>
                  {dayTasks.slice(0, 4).map((t) => {
                    const done = isTaskCompletedOnDate(t, date);
                    return (
                      <span
                        key={t._id}
                        className={`${style.calendarDot} ${style[t.color] || style.color1} ${
                          done ? style.calendarDotDone : ""
                        }`}
                        style={
                          t.color?.startsWith?.("#") ? { background: t.color } : undefined
                        }
                      />
                    );
                  })}
                  {dayTasks.length > 4 ? (
                    <span className={style.calendarMore}>+{dayTasks.length - 4}</span>
                  ) : null}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  const buildHistoryBuckets = () => {
    if (historyPeriod === "year") {
      const months = Array.from({ length: 12 }, (_, m) => ({
        label: new Date(2000, m, 1).toLocaleDateString("es-AR", { month: "short" }),
        total: 0,
        done: 0,
      }));
      historySummary.perUnit.forEach((u) => {
        const m = u.day.getMonth();
        months[m].total += u.total;
        months[m].done += u.completed;
      });
      return months.map((b) => ({
        ...b,
        percent: b.total ? Math.round((b.done / b.total) * 100) : 0,
      }));
    }

    return historySummary.perUnit.map((u) => ({
      label:
        historyPeriod === "week"
          ? WEEKDAY_LABELS[mondayIndex(u.day)]
          : String(u.day.getDate()),
      total: u.total,
      done: u.completed,
      percent: u.total ? Math.round((u.completed / u.total) * 100) : 0,
    }));
  };

  const renderHistory = () => {
    if (loading) return <p className={style.emptyMessage}>Cargando tareas...</p>;
    if (error) return <p className={style.errorMessage}>{error}</p>;

    const buckets = buildHistoryBuckets();

    return (
      <div className={style.historyMode}>
        <div className={style.historyControls}>
          <div className={style.historyPeriods}>
            {HISTORY_PERIODS.map((p) => (
              <button
                key={p.value}
                type="button"
                className={`${style.historyPeriodButton} ${
                  historyPeriod === p.value ? style.historyPeriodActive : ""
                }`}
                onClick={() => setHistoryPeriod(p.value)}
              >
                {p.label}
              </button>
            ))}
          </div>

          <div className={style.historyNav}>
            <button
              type="button"
              onClick={() => setHistoryRef((prev) => shiftPeriod(historyPeriod, prev, -1))}
              aria-label="Período anterior"
            >
              <FiChevronLeft />
            </button>
            <span>{formatPeriodLabel(historyPeriod, historyRef)}</span>
            <button
              type="button"
              onClick={() => setHistoryRef((prev) => shiftPeriod(historyPeriod, prev, 1))}
              aria-label="Período siguiente"
            >
              <FiChevronRight />
            </button>
          </div>
        </div>

        <div className={style.historyStats}>
          <div className={style.historyStat}>
            <strong>{historySummary.percent}%</strong>
            <span>rendimiento</span>
          </div>
          <div className={style.historyStat}>
            <strong>{historySummary.total}</strong>
            <span>tareas</span>
          </div>
          <div className={style.historyStat}>
            <strong>{historySummary.done}</strong>
            <span>hechas</span>
          </div>
          <div className={style.historyStat}>
            <strong>{historySummary.pending}</strong>
            <span>pendientes</span>
          </div>
        </div>

        {historySummary.total === 0 ? (
          <p className={style.emptyMessage}>No hay tareas en este período.</p>
        ) : (
          <>
            {/* Barras finas con % arriba y eje 0–100, como "Avance" en Métricas */}
            <div className={style.historyChart}>
              {(() => {
                const LEFT = 34;
                const COL = buckets.length > 12 ? 34 : 56;
                const BODY = 10;
                const TOP = 22;
                const H = 200;
                const BOTTOM = 26;
                const PLOT = H - TOP - BOTTOM;
                const W = LEFT + buckets.length * COL;
                const yVal = (pct) => TOP + PLOT * (1 - pct / 100);
                return (
                  <svg
                    className={style.historySvg}
                    width={W}
                    height={H}
                    viewBox={`0 0 ${W} ${H}`}
                    role="img"
                    aria-label="Porcentaje de tareas completadas por período"
                  >
                    {[0, 25, 50, 75, 100].map((pct) => (
                      <g key={pct}>
                        <line
                          x1={LEFT}
                          x2={W}
                          y1={yVal(pct)}
                          y2={yVal(pct)}
                          stroke="var(--border-color)"
                          strokeWidth="1"
                          opacity="0.5"
                        />
                        <text x={LEFT - 6} y={yVal(pct) + 3} textAnchor="end" className={style.historyAxis}>
                          {pct}%
                        </text>
                      </g>
                    ))}
                    {buckets.map((b, i) => {
                      const cx = LEFT + i * COL + COL / 2;
                      const top = yVal(b.percent);
                      const h = Math.max(b.total ? 3 : 0, yVal(0) - top);
                      return (
                        <g key={i}>
                          <title>{`${b.label}: ${b.done}/${b.total} (${b.percent}%)`}</title>
                          <rect
                            x={cx - BODY / 2}
                            y={yVal(0) - h}
                            width={BODY}
                            height={h}
                            rx="3"
                            fill="#75f94c"
                            opacity={b.total ? 1 : 0.25}
                          />
                          {b.total ? (
                            <text x={cx} y={Math.max(yVal(0) - h - 6, 10)} textAnchor="middle" className={style.historyValue}>
                              {b.percent}%
                            </text>
                          ) : null}
                          <text x={cx} y={H - 8} textAnchor="middle" className={style.historyAxis}>
                            {b.label}
                          </text>
                        </g>
                      );
                    })}
                  </svg>
                );
              })()}
            </div>

            <p className={style.historyLegend}>
              Cada barra muestra el <strong>% de tareas completadas</strong>
              {historyPeriod === "year"
                ? " en cada mes"
                : historyPeriod === "week"
                ? " en cada día de la semana"
                : historyPeriod === "month"
                ? " en cada día del mes"
                : " del día"}
              . Pasá el cursor por una barra para ver <strong>hechas / total</strong>.
            </p>
          </>
        )}
      </div>
    );
  };

  return (
    <div className={style.container}>
      <div className={style.headerShell}>
        <div className={style.headerCard}>
          <div className={style.headerTop}>
            {/* Barra compacta: fecha grande + ‹ › + HOY (según el mockup) */}
            <div className={style.headerDateGroup}>
              {viewMode === "day" ? (
                /* Filtros de la lista del día (la fecha vive en el panel izquierdo) */
                <div className={style.dayFilters} role="tablist" aria-label="Filtrar tareas del día">
                  {[
                    { value: "all", label: "Todas", count: null },
                    { value: "pending", label: "Pendientes", count: daySummary.pending },
                    { value: "done", label: "Completadas", count: daySummary.done },
                  ].map((f) => (
                    <button
                      key={f.value}
                      type="button"
                      role="tab"
                      aria-selected={dayFilter === f.value}
                      className={`${style.dayFilterBtn} ${
                        dayFilter === f.value ? style.dayFilterActive : ""
                      }`}
                      onClick={() => setDayFilter(f.value)}
                    >
                      {f.label}
                      {f.count !== null ? (
                        <span
                          className={`${style.dayFilterCount} ${
                            f.value === "done" ? style.dayFilterCountDone : style.dayFilterCountPending
                          }`}
                        >
                          {f.count}
                        </span>
                      ) : null}
                    </button>
                  ))}
                </div>
              ) : (
                <h1 className={style.dayTitle}>
                  {viewMode === "calendar" ? "Calendario" : "Historial"}
                </h1>
              )}
            </div>

            <div className={style.headerAside}>
              <div className={style.viewSwitch}>
                <button
                  type="button"
                  className={`${style.viewSwitchButton} ${
                    viewMode === "day" ? style.viewSwitchActive : ""
                  }`}
                  onClick={() => setViewMode("day")}
                >
                  <FiList />
                  Día
                </button>
                <button
                  type="button"
                  className={`${style.viewSwitchButton} ${
                    viewMode === "calendar" ? style.viewSwitchActive : ""
                  }`}
                  onClick={() => setViewMode("calendar")}
                >
                  <FiGrid />
                  Calendario
                </button>
                <button
                  type="button"
                  className={`${style.viewSwitchButton} ${
                    viewMode === "history" ? style.viewSwitchActive : ""
                  }`}
                  onClick={() => setViewMode("history")}
                >
                  <FiBarChart2 />
                  Historial
                </button>

                <button
                  type="button"
                  className={`${style.viewSwitchButton} ${style.viewSwitchAction}`}
                  onClick={() => handleOpenNewTask()}
                >
                  <FiPlus />
                  Nueva tarea
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className={style.tasksWorkspace}>
        <aside className={style.progressCard} aria-label="Progreso de tareas">
          {viewMode === "day" ? (
            <div className={style.panelDate}>
              <div className={style.panelDateRow}>
                <button
                  type="button"
                  className={style.dayNavBtn}
                  onClick={() => shiftSelectedDay(-1)}
                  aria-label="Día anterior"
                >
                  <FiChevronLeft />
                </button>
                <h1 className={style.panelDateTitle}>
                  {selectedDate.toLocaleDateString("es-AR", {
                    weekday: "long",
                    day: "numeric",
                    month: "long",
                    year: "numeric",
                  })}
                </h1>
                <button
                  type="button"
                  className={style.dayNavBtn}
                  onClick={() => shiftSelectedDay(1)}
                  aria-label="Día siguiente"
                >
                  <FiChevronRight />
                </button>
              </div>
              {/* Pill HOY: abre un calendario para saltar a cualquier día */}
              <DatePicker
                selected={selectedDate}
                onChange={(date) => {
                  if (date) setSelectedDate(date);
                  setIsPanelDatePickerOpen(false);
                }}
                customInput={
                  <button type="button" className={style.panelTodayBtn}>
                    <FiCalendar />
                    {isSameDay(selectedDate, new Date())
                      ? "Hoy"
                      : selectedDate.toLocaleDateString("es-AR", { day: "2-digit", month: "2-digit" })}
                    <FiChevronDown />
                  </button>
                }
                open={isPanelDatePickerOpen}
                onInputClick={() => setIsPanelDatePickerOpen((prev) => !prev)}
                onClickOutside={() => setIsPanelDatePickerOpen(false)}
                onCalendarClose={() => setIsPanelDatePickerOpen(false)}
                shouldCloseOnSelect
                popperClassName={style.taskDatepickerPopper}
                popperPlacement="bottom"
              />
            </div>
          ) : null}

          <div
            className={style.progressRing}
            style={{ "--progress": `${progressPercent}%` }}
          >
            <div className={style.progressRingInner}>
              <strong>{progressPercent}%</strong>
              <span>hecho</span>
            </div>
          </div>
          <span className={style.progressLabel}>
            {viewMode === "history"
              ? "Progreso del período"
              : viewMode === "calendar"
                ? "Progreso del mes"
                : "Progreso del día"}
          </span>

          <ul className={style.progressStatsList}>
            <li>
              <span className={`${style.progressStatDot} ${style.progressStatDotDone}`} />
              <span className={style.progressStatLabel}>Tareas completadas</span>
              <strong className={style.progressStatDone}>{completedTasksCount}</strong>
            </li>
            <li>
              <span className={`${style.progressStatDot} ${style.progressStatDotPending}`} />
              <span className={style.progressStatLabel}>Tareas pendientes</span>
              <strong className={style.progressStatPending}>{pendingTasksCount}</strong>
            </li>
          </ul>

          {comparativaMes.total > 0 || comparativaMes.totalAnt > 0 ? (
            <div className={style.comparativaCard}>
              <span className={style.panelIcon}>
                <FiTarget />
              </span>
              <p className={style.progresoComparativa}>
                Este mes cumpliste el <strong>{comparativaMes.actual}%</strong> de tus
                tareas · el mes pasado fue <strong>{comparativaMes.anterior}%</strong>
              </p>
            </div>
          ) : null}

          <div className={style.fraseCard}>
            <span className={style.panelIcon}>
              <FiTarget />
            </span>
            <div className={style.fraseBody}>
              <span className={style.fraseLabel}>Frase del día</span>
              <p className={style.fraseTexto}>“{fraseDelDia}”</p>
            </div>
          </div>
        </aside>

        <div className={style.tasksList}>
          {invitaciones.length > 0 ? (
            <div className={style.invList}>
              {invitaciones.map((inv) => (
                <div key={inv.id} className={style.invCard}>
                  <FiUsers className={style.invIcon} />
                  <p className={style.invText}>
                    <strong>{inv.de?.fullName || inv.de?.username || "Alguien"}</strong> te invitó a la tarea “{inv.meta}”
                  </p>
                  <button type="button" className={style.invAceptar} onClick={() => aceptarInvitacion(inv)}>
                    Aceptar
                  </button>
                  <button type="button" className={style.invRechazar} onClick={() => rechazarInvitacion(inv)} aria-label="Rechazar">
                    <FiX />
                  </button>
                </div>
              ))}
            </div>
          ) : null}
          {viewMode === "calendar"
            ? renderCalendar()
            : viewMode === "history"
              ? renderHistory()
              : renderContent()}
        </div>
      </div>

      {dayActionDate ? (
        <div
          className={style.taskModalOverlay}
          role="presentation"
          onMouseDown={() => setDayActionDate(null)}
        >
          <section
            className={style.dayActionSheet}
            role="dialog"
            aria-modal="true"
            aria-label="Acción del día"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <div className={style.dayActionHeader}>
              <p className={style.kicker}>Día seleccionado</p>
              <h2>
                {dayActionDate.toLocaleDateString("es-AR", {
                  weekday: "long",
                  day: "2-digit",
                  month: "long",
                })}
              </h2>
            </div>

            <div className={style.dayActionButtons}>
              <button
                type="button"
                className={style.dayActionPrimary}
                onClick={() => {
                  const date = dayActionDate;
                  setDayActionDate(null);
                  setSelectedDate(date);
                  handleOpenNewTask(date);
                }}
              >
                <FiPlus />
                Crear tarea
              </button>

              <button
                type="button"
                className={style.dayActionSecondary}
                onClick={() => {
                  const date = dayActionDate;
                  setDayActionDate(null);
                  setSelectedDate(date);
                  setViewMode("day");
                }}
              >
                <FiList />
                Ver tareas del día
              </button>
            </div>
          </section>
        </div>
      ) : null}

      {isTaskModalOpen ? (
        <div className={style.taskModalOverlay} role="presentation" onMouseDown={handleCloseTaskModal}>
          <section
            className={style.taskModal}
            role="dialog"
            aria-modal="true"
            aria-label={editingTask ? "Editar tarea" : "Nueva tarea"}
            onMouseDown={(event) => event.stopPropagation()}
          >
            <div className={style.taskModalHeader}>
              <div>
                <p className={style.kicker}>Tarea</p>
                <h2>{editingTask ? "Editar tarea" : "Nueva tarea"}</h2>
              </div>
              <button type="button" className={style.closeModalButton} onClick={handleCloseTaskModal}>
                <FiX />
              </button>
            </div>

            {formError ? <p className={style.modalError}>{formError}</p> : null}
            {formSuccess ? <p className={style.modalSuccess}>{formSuccess}</p> : null}

            <form className={style.taskForm} onSubmit={handleTaskSubmit}>
              <label className={style.formField}>
                <span>Tarea</span>
                <input
                  name="meta"
                  type="text"
                  value={formData.meta}
                  onChange={handleFormChange}
                  placeholder="Ej: Revisar pedidos"
                />
              </label>

              <div className={style.formField}>
                <DatePicker
                  selected={formData.fecha}
                  onChange={handleTaskDateChange}
                  dateFormat="dd-MM-yyyy"
                  customInput={<FormDateButton />}
                  open={isTaskDatePickerOpen}
                  onInputClick={() => setIsTaskDatePickerOpen((prev) => !prev)}
                  onClickOutside={() => setIsTaskDatePickerOpen(false)}
                  onCalendarClose={() => setIsTaskDatePickerOpen(false)}
                  shouldCloseOnSelect
                  popperClassName={style.taskDatepickerPopper}
                  popperPlacement="bottom"
                >
                  {/* Pie del calendario: Hoy y Cancelar */}
                  <div className={style.datepickerFoot}>
                    <button
                      type="button"
                      className={style.datepickerFootBtn}
                      onClick={() => handleTaskDateChange(new Date())}
                    >
                      Hoy
                    </button>
                    <button
                      type="button"
                      className={`${style.datepickerFootBtn} ${style.datepickerFootCancel}`}
                      onClick={() => setIsTaskDatePickerOpen(false)}
                    >
                      Cancelar
                    </button>
                  </div>
                </DatePicker>
              </div>

              <div className={style.formField}>
                <span>Momento</span>
                <div className={style.momentoGrid}>
                  <button
                    type="button"
                    className={`${style.momentoButton} ${
                      formData.momento === "exacta" ? style.momentoButtonActive : ""
                    }`}
                    onClick={() => handleMomentoSelect("exacta")}
                  >
                    <FiClock />
                    Hora exacta
                  </button>
                  {MOMENTO_OPTIONS.map(({ value, label, Icon }) => (
                    <button
                      key={value}
                      type="button"
                      className={`${style.momentoButton} ${
                        formData.momento === value ? style.momentoButtonActive : ""
                      }`}
                      onClick={() => handleMomentoSelect(value)}
                    >
                      <Icon />
                      {label}
                    </button>
                  ))}
                </div>

                {formData.momento === "exacta" ? (
                  <div className={style.horaRow} aria-label="Hora exacta">
                    <select
                      className={style.horaSelect}
                      aria-label="Hora"
                      value={(isExactTime(formData.horario) ? formData.horario : "09:00").split(":")[0]}
                      onChange={(e) => {
                        const min = (isExactTime(formData.horario) ? formData.horario : "09:00").split(":")[1];
                        setFormData((prev) => ({ ...prev, horario: `${e.target.value}:${min}` }));
                      }}
                    >
                      {Array.from({ length: 24 }, (_, i) => String(i).padStart(2, "0")).map((h) => (
                        <option key={h} value={h}>{h}</option>
                      ))}
                    </select>
                    <span className={style.horaSep}>:</span>
                    <select
                      className={style.horaSelect}
                      aria-label="Minutos"
                      value={(isExactTime(formData.horario) ? formData.horario : "09:00").split(":")[1]}
                      onChange={(e) => {
                        const hr = (isExactTime(formData.horario) ? formData.horario : "09:00").split(":")[0];
                        setFormData((prev) => ({ ...prev, horario: `${hr}:${e.target.value}` }));
                      }}
                    >
                      {Array.from({ length: 12 }, (_, i) => String(i * 5).padStart(2, "0")).map((m) => (
                        <option key={m} value={m}>{m}</option>
                      ))}
                    </select>
                    <span className={style.horaHint}>hs</span>
                  </div>
                ) : null}
              </div>

              <div className={style.formField}>
                <span>Prioridad</span>
                <div className={style.momentoGrid}>
                  {prioridades.map((p) => {
                    const activa =
                      (formData.urgencia || "").toLowerCase() === p.nombre.toLowerCase();
                    return (
                      <button
                        key={p.nombre}
                        type="button"
                        className={`${style.momentoButton} ${style.prioridadChip} ${
                          activa ? style.prioridadChipActive : ""
                        }`}
                        style={activa ? { borderColor: p.color, color: p.color } : undefined}
                        onClick={() => setFormData((prev) => ({ ...prev, urgencia: p.nombre }))}
                      >
                        <span className={style.prioridadDot} style={{ background: p.color }} />
                        {p.nombre}
                        <span
                          role="button"
                          tabIndex={0}
                          className={style.prioridadEditar}
                          title="Cambiar nombre o color"
                          onClick={(e) => {
                            e.stopPropagation();
                            abrirEditarPrioridad(p);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              e.stopPropagation();
                              abrirEditarPrioridad(p);
                            }
                          }}
                        >
                          <FiEdit2 />
                        </span>
                      </button>
                    );
                  })}
                  <button
                    type="button"
                    className={`${style.momentoButton} ${style.prioridadNueva}`}
                    onClick={abrirNuevaPrioridad}
                  >
                    <FiPlus />
                    Nueva prioridad
                  </button>
                </div>

                {prioridadEditor ? (
                  <div className={style.prioridadEditor}>
                    <input
                      type="text"
                      value={prioridadEditor.nombre}
                      maxLength={30}
                      placeholder="Nombre de la prioridad"
                      autoFocus
                      onChange={(e) =>
                        setPrioridadEditor((prev) => ({ ...prev, nombre: e.target.value }))
                      }
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          confirmarPrioridad();
                        }
                      }}
                    />
                    {/* 5 colores principales + los elegidos a mano + "+" para otro */}
                    <div className={style.prioridadColores}>
                      {[
                        ...PRIORIDAD_COLORES.slice(0, 5),
                        ...prioridadColoresExtra,
                        ...(PRIORIDAD_COLORES.slice(0, 5).includes(prioridadEditor.color) ||
                        prioridadColoresExtra.includes(prioridadEditor.color)
                          ? []
                          : [prioridadEditor.color]),
                      ].map((c) => (
                        <button
                          key={c}
                          type="button"
                          className={`${style.prioridadColor} ${
                            prioridadEditor.color === c ? style.prioridadColorActivo : ""
                          }`}
                          style={{ background: c }}
                          onClick={() => setPrioridadEditor((prev) => ({ ...prev, color: c }))}
                          aria-label={`Color ${c}`}
                        />
                      ))}
                      <label className={style.prioridadColorMas} title="Elegir otro color">
                        <FiPlus />
                        <input
                          type="color"
                          value={prioridadEditor.color}
                          onChange={(e) => {
                            const c = e.target.value.toLowerCase();
                            setPrioridadColoresExtra((prev) =>
                              PRIORIDAD_COLORES.slice(0, 5).includes(c) || prev.includes(c)
                                ? prev
                                : [...prev, c]
                            );
                            setPrioridadEditor((prev) => ({ ...prev, color: c }));
                          }}
                        />
                      </label>
                    </div>
                    {prioridadError ? (
                      <p className={style.prioridadErrorTexto}>{prioridadError}</p>
                    ) : null}
                    <div className={style.prioridadEditorAcciones}>
                      {prioridadEditor.original ? (
                        <button
                          type="button"
                          className={style.prioridadBtnEliminar}
                          onClick={eliminarPrioridad}
                        >
                          <FiTrash2 /> Eliminar
                        </button>
                      ) : null}
                      <button
                        type="button"
                        className={style.prioridadBtnCancelar}
                        onClick={() => setPrioridadEditor(null)}
                      >
                        Cancelar
                      </button>
                      <button
                        type="button"
                        className={style.prioridadBtnGuardar}
                        onClick={confirmarPrioridad}
                      >
                        {prioridadEditor.original ? "Guardar cambios" : "Crear prioridad"}
                      </button>
                    </div>
                  </div>
                ) : null}
              </div>

              <div className={style.formField}>
                <span>Color</span>
                {/* 5 colores principales + los elegidos a mano + "+" para otro */}
                <div className={style.taskColorPicker}>
                  {[
                    ...colorOptions.slice(0, 5),
                    ...taskColoresExtra,
                    ...(colorOptions.slice(0, 5).includes(formData.color) ||
                    taskColoresExtra.includes(formData.color) ||
                    !formData.color
                      ? []
                      : [formData.color]),
                  ].map((color) => {
                    const esHex = color.startsWith("#");
                    return (
                      <button
                        key={color}
                        type="button"
                        className={`${style.taskColorButton} ${esHex ? "" : style[color]} ${
                          formData.color === color ? style.taskColorSelected : ""
                        }`}
                        style={esHex ? { background: color } : undefined}
                        onClick={() => handleColorSelect(color)}
                        aria-label={`Elegir color ${color}`}
                      />
                    );
                  })}
                  <label className={style.taskColorMas} title="Elegir otro color">
                    <FiPlus />
                    <input
                      type="color"
                      value={formData.color?.startsWith?.("#") ? formData.color : "#5dc72d"}
                      onChange={(e) => {
                        const c = e.target.value.toLowerCase();
                        setTaskColoresExtra((prev) => (prev.includes(c) ? prev : [...prev, c]));
                        handleColorSelect(c);
                      }}
                    />
                  </label>
                </div>
              </div>

              <label className={style.repeatToggle}>
                <input
                  name="esRecurrente"
                  type="checkbox"
                  checked={formData.esRecurrente}
                  onChange={handleFormChange}
                />
                <span>Repetir tarea</span>
              </label>

              {formData.esRecurrente ? (
                <div className={style.daysPicker}>
                  {["D", "L", "M", "MI", "J", "V", "S"].map((dia) => (
                    <label key={dia} className={style.dayChip}>
                      <input
                        type="checkbox"
                        checked={getTaskRepeatDays(formData.diasRepeticion).includes(dia)}
                        onChange={() => toggleRepeatDay(dia)}
                      />
                      <span>{dia}</span>
                    </label>
                  ))}
                </div>
              ) : null}

              <div className={style.modalActions}>
                <button type="button" className={style.cancelButton} onClick={handleCloseTaskModal}>
                  Cancelar
                </button>
                <button type="submit" className={style.saveTaskButton} disabled={formSaving}>
                  {formSaving ? "Guardando..." : editingTask ? "Guardar cambios" : "Crear tarea"}
                </button>
              </div>
            </form>
          </section>
        </div>
      ) : null}

      {compartirTask ? (
        <CompartirTareaModal
          task={compartirTask}
          onClose={() => setCompartirTask(null)}
          onCambio={fetchTasks}
        />
      ) : null}
    </div>
  );
}

export default Tareas;
